import { z } from "zod";

/**
 * Per-practice message routing for the clinician inbox.
 *
 * Categories are an everpresent, fixed set — a patient always self-categorizes when opening a ticket.
 * Routing is decided *per category*: each category picks a `routing` path (no routing / admin only /
 * custom recipients). The ticket surfaces in the inbox of whoever the category routes to.
 */

export const MESSAGE_CATEGORIES = [
  "clinical",
  "prescriptions",
  "scheduling",
  "admin",
  "other",
] as const;
export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number];

/** Human display labels for the (finite) category codes — explicit map, not mechanical title-casing. */
export const MESSAGE_CATEGORY_LABELS: Record<MessageCategory, string> = {
  clinical: "Clinical",
  prescriptions: "Prescriptions",
  scheduling: "Scheduling",
  admin: "Admin",
  other: "Other",
};

/**
 * Patient-friendly category labels (fuller phrasing than the clinician-terse `MESSAGE_CATEGORY_LABELS`).
 * Shown to patients when they open a ticket, on both web and mobile.
 */
export const PATIENT_CATEGORY_LABELS: Record<MessageCategory, string> = {
  clinical: "Clinical question",
  prescriptions: "Prescriptions & supplements",
  scheduling: "Appointment scheduling",
  admin: "Administrative",
  other: "Something else",
};

/**
 * Where a single category routes:
 * - `none`: no routing — the ticket lands in everyone's inbox (a shared queue).
 * - `admin`: routes to the practice's admins (their refs are snapshotted into `recipientRefs`).
 * - `custom`: routes to the explicitly chosen teammates in `recipientRefs`.
 */
export const ROUTING_PATHS = ["none", "admin", "custom"] as const;
export type RoutingPath = (typeof ROUTING_PATHS)[number];

export const ROUTING_PATH_LABELS: Record<RoutingPath, string> = {
  none: "No routing",
  admin: "Route to Admin only",
  custom: "Custom routing",
};

export interface MessageCategoryOption {
  code: MessageCategory;
  label: string;
}

/**
 * The categories a patient can pick when opening a ticket — always the full fixed set, in pool order,
 * with patient-friendly labels. Categories are everpresent, so a patient always self-categorizes.
 */
export function getActiveCategoryOptions(): MessageCategoryOption[] {
  return MESSAGE_CATEGORIES.map((code) => ({ code, label: PATIENT_CATEGORY_LABELS[code] }));
}

/**
 * Categories offered as clinician inbox filter pills / tag options — always the full fixed set.
 * Categories are everpresent (independent of how each routes).
 */
export function getConfiguredCategories(): MessageCategory[] {
  return [...MESSAGE_CATEGORIES];
}

/** Team classes used to bulk-select recipients: clinician vs admin/staff. */
export const ROUTING_ROLES = ["clinician", "admin"] as const;
export type RoutingRole = (typeof ROUTING_ROLES)[number];

export const routingRuleSchema = z.object({
  category: z.enum(MESSAGE_CATEGORIES),
  /** How this category routes. See {@link ROUTING_PATHS}. */
  routing: z.enum(ROUTING_PATHS).default("none"),
  /**
   * Provider references (`Practitioner/<id>`) who receive this category — the ticket lands in each
   * recipient's inbox. Used when `routing` is `admin` (snapshot of the admins) or `custom`. Ignored
   * when `routing` is `none` (everyone sees it).
   */
  recipientRefs: z.array(z.string()).default([]),
});
export type RoutingRule = z.infer<typeof routingRuleSchema>;

/** 6-digit hex color, e.g. #F7D252. */
export const CATEGORY_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const categoryColorSchema = z.string().regex(CATEGORY_COLOR_REGEX, "Must be a 6-digit hex color");

export const routingConfigSchema = z.object({
  version: z.literal(1).default(1),
  /** One rule per category. Missing categories are treated as `none` (see {@link getCategoryRoutingRule}). */
  rules: z.array(routingRuleSchema).default([]),
  /** Hours a conversation may sit in needs-reply before the SLA aging/escalation flags it. */
  slaHours: z.number().int().min(1).max(168).default(24),
  /**
   * Per-category tag colors (hex). Display metadata, independent of routing. Missing categories fall
   * back to the neutral default. Partial so a practice only stores the categories it has customized.
   */
  categoryColors: z.partialRecord(z.enum(MESSAGE_CATEGORIES), categoryColorSchema).default({}),
});
export type RoutingConfig = z.infer<typeof routingConfigSchema>;

/** A fresh practice starts with all categories present and no routing (every message is shared). */
export const DEFAULT_ROUTING_CONFIG: RoutingConfig = {
  version: 1,
  slaHours: 24,
  rules: MESSAGE_CATEGORIES.map((category) => ({
    category,
    routing: "none" as const,
    recipientRefs: [],
  })),
  categoryColors: {},
};

/** The routing rule for a category, or a `none` default when the practice hasn't stored one. */
export function getCategoryRoutingRule(
  config: RoutingConfig | undefined,
  category: MessageCategory,
): RoutingRule {
  return (
    config?.rules.find((rule) => rule.category === category) ?? {
      category,
      routing: "none",
      recipientRefs: [],
    }
  );
}

/**
 * Categories the given provider receives — drives their "My inbox" view. A provider receives a
 * category when it has no routing (everyone), or when they're one of its recipients (admin / custom).
 * Falls back to all categories when no provider is given, so a lookup never locks anyone out.
 */
export function getRecipientCategories(
  config: RoutingConfig | undefined,
  providerRef: string | undefined,
): MessageCategory[] {
  if (!providerRef) return [...MESSAGE_CATEGORIES];
  return MESSAGE_CATEGORIES.filter((category) => {
    const rule = getCategoryRoutingRule(config, category);
    // No routing = shared: everyone's inbox. Otherwise only the routed recipients.
    if (rule.routing === "none") return true;
    return rule.recipientRefs.includes(providerRef);
  });
}

/**
 * The customized tag color for a category, or undefined to use the neutral default. Accepts a raw
 * category string (e.g. from an Encounter) and narrows it internally.
 */
export function getCategoryColor(
  config: RoutingConfig | undefined,
  category: string | undefined,
): string | undefined {
  if (!category) return undefined;
  return config?.categoryColors?.[category as MessageCategory];
}

export function parseRoutingConfig(raw: string | undefined | null): RoutingConfig {
  if (!raw?.length) return DEFAULT_ROUTING_CONFIG;
  try {
    const parsed = routingConfigSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_ROUTING_CONFIG;
  } catch {
    return DEFAULT_ROUTING_CONFIG;
  }
}

/**
 * The coarse team class a category maps to: clinical questions go to a clinician, everything else to
 * admin/staff. Single source of truth for the category→role default — used by the patient-facing
 * "connected provider" avatar on the mobile tickets route.
 */
export function roleForCategory(category: MessageCategory): RoutingRole {
  return category === "clinical" ? "clinician" : "admin";
}

/** Whether a needs-reply conversation has breached the SLA window. */
export function isSlaBreached({
  config,
  needsReplySince,
  now = new Date(),
}: {
  config: RoutingConfig;
  needsReplySince: string | undefined;
  now?: Date;
}): boolean {
  if (!needsReplySince) return false;
  const since = new Date(needsReplySince).getTime();
  if (Number.isNaN(since)) return false;
  const elapsedHours = (now.getTime() - since) / (1000 * 60 * 60);
  return elapsedHours >= config.slaHours;
}
